import { useCallback } from 'react';

// Public pricing for /pricing, /services/:slug and /book.
// LOCAL SAMPLE DATA ONLY: no Backend calls. Ranges are illustrative examples for this
// template, not real offers, quotes, charges or payment amounts. Replace them with your own.
export interface PublicServicePrice {
  slug: string; name: string; min_cents: number; max_cents: number; currency: string; updated_at: string;
}
export interface PublicDiagnosticFee {
  amount_cents: number; currency: string; credited_toward_repair: boolean; explanation: string | null;
}
export interface PublicPricing {
  published: boolean;
  policy_version?: string | null;
  disclaimer: string | null;
  diagnostic_fee: PublicDiagnosticFee | null;
  services: PublicServicePrice[];
}

export const DEFAULT_DISCLAIMER = 'Example ranges for illustration only, not a quote or offer. Your technician confirms a final quote after diagnosing the issue.';
export const UNPUBLISHED_RANGE = 'Pricing confirmed after diagnosis';
export const CREDIT_COPY = 'Any diagnostic visit fee and how it is applied are confirmed with you before your visit.';

export const formatUsd = (cents: number) => {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2,
  }).format(cents / 100);
};
export const formatRange = (p: { min_cents: number; max_cents: number }) => (
  p.min_cents === p.max_cents ? formatUsd(p.min_cents) : `${formatUsd(p.min_cents)}–${formatUsd(p.max_cents)}`
);

const SAMPLE = '2026-01-01';
const SAMPLE_PRICING: PublicPricing = {
  published: true,
  policy_version: null,
  disclaimer: DEFAULT_DISCLAIMER,
  // No fee is stated: the template makes no fee promise. Owners can add their own terms here.
  diagnostic_fee: null,
  services: [
    { slug: 'refrigerator-repair', name: 'Refrigerator Repair', min_cents: 15000, max_cents: 45000, currency: 'USD', updated_at: SAMPLE },
    { slug: 'washer-dryer-repair', name: 'Washer & Dryer Repair', min_cents: 12000, max_cents: 35000, currency: 'USD', updated_at: SAMPLE },
    { slug: 'dishwasher-repair', name: 'Dishwasher Repair', min_cents: 12000, max_cents: 32000, currency: 'USD', updated_at: SAMPLE },
    { slug: 'oven-range-repair', name: 'Oven & Range Repair', min_cents: 13000, max_cents: 40000, currency: 'USD', updated_at: SAMPLE },
    { slug: 'air-conditioning-repair', name: 'Air Conditioning Repair', min_cents: 15000, max_cents: 50000, currency: 'USD', updated_at: SAMPLE },
  ],
};

export function fetchPublicPricing(): Promise<PublicPricing> {
  return Promise.resolve(SAMPLE_PRICING);
}

// Same shape as before so existing components keep working; always 'ready', never loads remotely.
export function usePublicPricing() {
  const state = 'ready' as 'loading' | 'ready' | 'error';
  const data: PublicPricing | null = SAMPLE_PRICING;
  const reload = useCallback(() => undefined, []);
  const priceFor = useCallback((slug: string) => SAMPLE_PRICING.services.find((s) => s.slug === slug) ?? null, []);
  return { state, data, reload, priceFor };
}